import {
  IonButton,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFab,
  IonFabButton,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonMenu,
  IonMenuToggle,
  IonRadio,
  IonRadioGroup,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  RefresherEventDetail,
} from '@ionic/react';
import { add, arrowDownOutline, caretDown, caretUp, close, closeOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';

import { IBranch } from '../../../models/dms/branch.model';
import { IRepairOrder } from '../../../models/dms/repairOrder.model';
import { RouteNames } from '../../../models/routes.model';
import { useDataSync } from '../../../providers/DataSyncProvider';
import store from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setRepairOrderHasSyncStoreAsync } from '../../../store/repairOrderSyncSlice';
import FormSelectable from '../../form/formSelectable/FormSelectable';
import RepairOrderCard from '../../repairOrderCard/RepairOrderCard';
import RepairOrderCardSkeleton from '../../repairOrderCard/RepairOrderCardSkeleton';
import { IonContentWrapper } from './JobSearchContent.styles';
import moment from 'moment';
import {
  setFiltersOrSortsStoreAsync,
  setOrderByDateDescendingState,
  setOrderByDateDescendingStoreAsync,
  setSelectedBranchCodeStoreAsync,
  setTechCodeCheckboxState,
  setTechCodeCheckboxStoreAsync,
  setTodaysBookingsCheckboxState,
  setTodaysBookingsCheckboxStoreAsync,
} from '../../../store/filterMenuSlice';

interface IComponentProps {
  handleClickRepairOrder: (e: React.MouseEvent, repairOrder: IRepairOrder) => void;
  handleFilterChanged: (extended: boolean) => void;
  hasSearched?: boolean;
  searchKeyword?: string;
}

interface IFormInputs {
  branchCode: string;
}

const JobSearchContent: React.FC<IComponentProps> = ({ handleClickRepairOrder, handleFilterChanged, hasSearched, searchKeyword }) => {
  const [repairOrdersDisplay, setRepairOrdersDisplay] = useState<{ date: string; repairOrders: IRepairOrder[] }[]>([]);

  const [moreRepairOrdersExist, setMoreRepairOrdersExist] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [selectedBranch, setSelectedBranch] = useState<IBranch>();
  const [refreshEvent, setRefreshEvent] = useState<CustomEvent<RefresherEventDetail>>();
  const {
    state: { repairOrderSyncTimer },
  } = useDataSync();

  const branchSearchTerm = useRef('');
  const [branchFiltered, setBranchFiltered] = useState<IBranch[]>([]);
  const history = useHistory();
  const [todayString] = useState(moment().format('YYYY-MM-DD'));

  const { repairOrders } = useAppSelector((state) => state.repairOrder);
  const { repairOrderSyncLoading } = useAppSelector((state) => state.repairOrderSync);
  const { repairOrderSearchLoading } = useAppSelector((state) => state.repairOrder);
  const { branchesSync, branchesSyncLoading } = useAppSelector((state) => state.branchSync);
  const { techCodeCheckbox, selectedBranchCode, todaysBookingsCheckbox, orderByDateDescending } = useAppSelector((state) => state.filterMenu);
  const [filterOrSortTriggered, setFilterOrSortTriggered] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    resetScrollPaging();
    if (repairOrders?.length) {
      generatePagedResult(repairOrders, page);
    } else {
      generatePagedResult([], page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrders]);

  useEffect(() => {
    if (repairOrders?.length) {
      generatePagedResult(repairOrders, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!repairOrderSyncLoading && refreshEvent) {
      refreshEvent.detail.complete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrderSyncLoading]);

  useEffect(() => {
    setValue('branchCode', selectedBranchCode);
    const { branchesSync } = store.getState().branchSync;
    const matchingBranch = branchesSync?.data?.items?.find((br) => br.code === selectedBranchCode);
    setSelectedBranch(matchingBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchCode]);

  const doRefresh = (event?: CustomEvent<RefresherEventDetail>) => {
    resetScrollPaging();

    dispatch(setRepairOrderHasSyncStoreAsync(false));
    repairOrderSyncTimer?.fire();

    // Add a fake "loading" progress to make it look like the app is working ;)
    setTimeout(() => {
      setRefreshEvent(event);
    }, 100);
  };

  useEffect(() => {
    handleFilterBranchForSelectable(setBranchFiltered, branchesSync?.data?.items || []);
  }, [branchesSync]);

  useEffect(() => {
    let filtersOrSortsTmp = 0;

    // Branch
    if (selectedBranch && selectedBranch.code) {
      filtersOrSortsTmp++;
    }

    // Tech Code Checkbox
    if (techCodeCheckbox) {
      filtersOrSortsTmp++;
    }

    // Todays Bookings Checkbox
    if (todaysBookingsCheckbox) {
      filtersOrSortsTmp++;
    }

    // Order By Date Descending
    if (orderByDateDescending) {
      filtersOrSortsTmp++;
    }

    // Dispatch the action to update filtersOrSorts state
    dispatch(setFiltersOrSortsStoreAsync(filtersOrSortsTmp));

    setFilterOrSortTriggered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOrSortTriggered]);

  const generatePagedResult = (repairOrders: IRepairOrder[], page: number) => {
    const roResult = [...(repairOrders || [])].filter((ro) => !ro.isAdhoc);
    const roGrouped = roResult.reduce((result, item) => {
      const date = moment(item.dropOffDateTime).format('YYYY-MM-DD');
      if (!result[date]) {
        result[date] = [];
      }
      result[date].push(item);
      return result;
    }, {} as { [key: string]: IRepairOrder[] });

    const roGroupedSorted = Object.entries(roGrouped).map(([date, repairOrders]) => ({
      date,
      repairOrders,
    }));

    const roResultPaged = roGroupedSorted.slice(0, page * perPage + perPage);
    setRepairOrdersDisplay(roResultPaged);
    setMoreRepairOrdersExist((repairOrders || []).length > page * perPage + perPage);
  };

  const moreRepairOrders = () => {
    // If there are more records than the current displayed records, load more
    var recordCount = (repairOrders || []).length;
    if (recordCount > page * perPage + perPage) {
      setPage(page + 1);
    }
  };

  const extendSearch = () => {
    handleFilterChanged(true);
  };

  const resetScrollPaging = () => {
    setRepairOrdersDisplay([]);
    if (page >= 1) {
      // This will kick off a refresh from the useEffect
      setPage(0);
    } else {
      // Manually kick off a refresh
      if (repairOrders?.length) {
        generatePagedResult(repairOrders, 0);
      }
    }
  };

  const handleClickAdhocClocking = () => {
    history.replace({
      pathname: `${RouteNames.adhocClocking}`,
    });
  };

  const handleApplyFilterClick = async () => {
    const selectedBranch = getValues('branchCode');
    await dispatch(setSelectedBranchCodeStoreAsync(selectedBranch));
    setFilterOrSortTriggered(true);
    handleFilterChanged(false);
    doRefresh();
  };

  const handleClearBranchSelectionClick = async () => {
    setValue('branchCode', '');
    await dispatch(setSelectedBranchCodeStoreAsync(''));
  };

  const { control, getValues, setValue } = useForm<IFormInputs>({
    defaultValues: {
      branchCode: undefined,
    },
    mode: 'onSubmit',
  });

  const handleFilterBranchForSelectable = (setState: (value: React.SetStateAction<IBranch[]>) => void, data: IBranch[], term?: string) => {
    branchSearchTerm.current = term === undefined ? branchSearchTerm.current : term;
    const filteredData = data.filter((b) => b.code?.toLocaleLowerCase().includes(branchSearchTerm.current.toLocaleLowerCase()) || b.name?.toLocaleLowerCase().includes(branchSearchTerm.current.toLocaleLowerCase()));
    setState(filteredData);
  };

  const handleCheckboxChange = (event: CustomEvent) => {
    const newValue = !techCodeCheckbox;
    dispatch(setTechCodeCheckboxStoreAsync(newValue));
  };

  const handleTodaysBookingsCheckboxChange = (event: CustomEvent) => {
    const newValue = !todaysBookingsCheckbox;
    dispatch(setTodaysBookingsCheckboxStoreAsync(newValue));
  };

  const groupKeyDisplay = (date: string) => {
    const formattedDate = moment(date, 'YYYY-MM-DD').format('ddd, DD MMM YYYY');
    return date === todayString ? 'Today' : formattedDate;
  };

  const handleSortOrderChange = () => {
    dispatch(setOrderByDateDescendingStoreAsync(!orderByDateDescending));
  };

  const handleClearBranchSelection = async () => {
    setValue('branchCode', '');
    await dispatch(setSelectedBranchCodeStoreAsync(''));
  };

  const handleClearFilters = async () => {
    await handleClearBranchSelection();
    dispatch(setTechCodeCheckboxState(false));
    dispatch(setTodaysBookingsCheckboxState(false));
    dispatch(setOrderByDateDescendingState(false));
    await handleApplyFilterClick();
    setFilterOrSortTriggered(true);
  };

  const processedBranchFiltered = branchFiltered
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }))
    .map((branch) => ({
      ...branch,
      name: `${branch.code} - ${branch.name}`,
    }));

  return (
    <>
      <IonContentWrapper color="lightest" id="jobsearch-content">
        <IonMenu menuId="jobsearch-filter" side="end" contentId="jobsearch-content" type="overlay">
          <IonContent style={{ pointerEvents: 'auto' }}>
            <IonList>
              <IonItemDivider>
                <IonLabel>Filter By</IonLabel>
              </IonItemDivider>
              <div className="flexed row flexed-center ion-padding-start">
                <div style={{ overflow: 'hidden' }}>
                  <FormSelectable
                    control={control}
                    label="Branch"
                    name="branchCode"
                    itemKey="code"
                    itemValue="name"
                    placeholder="Select a Branch"
                    items={processedBranchFiltered || []}
                    itemsLoading={branchesSyncLoading}
                    allowSearch
                    handleSearch={(term) => handleFilterBranchForSelectable(setBranchFiltered, branchesSync?.data?.items || [], term)}
                  />
                </div>
                <IonButton fill="clear" slot="end" onClick={() => handleClearBranchSelectionClick()}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </div>
              <IonItem lines="none" className="check-box-item">
                <IonCheckbox justify="space-between" checked={techCodeCheckbox} onIonChange={handleCheckboxChange}>
                  Jobs allocated to technician
                </IonCheckbox>
              </IonItem>
              <IonItem lines="none" className="check-box-item">
                <IonCheckbox justify="space-between" checked={todaysBookingsCheckbox} onIonChange={handleTodaysBookingsCheckboxChange}>
                  Today's bookings
                </IonCheckbox>
              </IonItem>
            </IonList>
            <IonItemDivider>
              <IonLabel>Sort By</IonLabel>
            </IonItemDivider>

            <IonRadioGroup className="width-100pc" value={orderByDateDescending}>
              <IonItem lines="none" onClick={() => handleSortOrderChange()}>
                {orderByDateDescending ? <IonIcon icon={caretDown} slot="end" /> : <IonIcon icon={caretUp} slot="end" />}
                <IonRadio slot="start" value={orderByDateDescending} labelPlacement="end" justify="space-between">
                  Arrival Date
                </IonRadio>
              </IonItem>
            </IonRadioGroup>

            <IonMenuToggle menu="jobsearch-filter">
              <div className="flexed">
                <IonButton expand="full" onClick={() => handleApplyFilterClick()} className="flexed-fill">
                  Apply Filter
                </IonButton>
                <IonButton expand="full" onClick={() => handleClearFilters()}>
                  Clear
                </IonButton>
                <IonButton expand="full" fill="clear">
                  <IonIcon icon={close} slot="icon-only" />
                </IonButton>
              </div>
            </IonMenuToggle>
          </IonContent>
        </IonMenu>

        <IonRefresher slot="fixed" onIonRefresh={(event) => doRefresh(event)}>
          <IonRefresherContent pullingIcon={arrowDownOutline} pullingText="" refreshingSpinner={'lines'} refreshingText="" />
        </IonRefresher>

        <IonGrid>
          {repairOrdersDisplay?.map(({ date, repairOrders }) => (
            <div key={date}>
              <IonItemDivider>
                <IonLabel>{groupKeyDisplay(date)}</IonLabel>
              </IonItemDivider>
              <IonRow>
                {repairOrders.map((repairOrder: IRepairOrder) => (
                  <IonCol key={repairOrder.repairOrderNumber} className="flexed" sizeXs="12" sizeSm="12" sizeMd="6" sizeLg="6" sizeXl="4">
                    <RepairOrderCard key={repairOrder.repairOrderNumber} repairOrder={repairOrder} onClick={(e) => handleClickRepairOrder(e, repairOrder)} />
                  </IonCol>
                ))}
              </IonRow>
            </div>
          ))}
          {(repairOrderSyncLoading || repairOrderSearchLoading) && (
            <IonCol sizeXs="12" sizeSm="12" sizeMd="6" sizeLg="6" sizeXl="4">
              <RepairOrderCardSkeleton />
            </IonCol>
          )}
        </IonGrid>
        {moreRepairOrdersExist && (
          <div className="more-results-container ion-margin-vertical">
            <IonButton shape="round" onClick={() => moreRepairOrders()}>
              show more results
            </IonButton>
          </div>
        )}
        {repairOrdersDisplay?.length === 0 ? (
          <IonLabel color="medium" className="flexed ion-justify-content-center ion-margin-top">
            no repair orders found
          </IonLabel>
        ) : (
          <></>
        )}
        {!(repairOrderSyncLoading || repairOrderSearchLoading) && hasSearched && searchKeyword && (
          <>
            {!repairOrderSearchLoading && (
              <div className="more-results-container ion-margin-vertical">
                <IonButton shape="round" onClick={() => extendSearch()}>
                  Extend Search
                </IonButton>
              </div>
            )}
            <IonFab vertical="bottom" horizontal="end" slot="fixed" onClick={() => handleClickAdhocClocking()}>
              <IonFabButton color="tertiary">
                <IonIcon icon={add} />
              </IonFabButton>
            </IonFab>
          </>
        )}
      </IonContentWrapper>
    </>
  );
};

export default JobSearchContent;
